import {
    Anchor,
    Button,
    Checkbox,
    Group,
    MultiSelect,
    Radio,
    SimpleGrid,
    Stack,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import type { EstablishmentOfferEnum } from '@routellers/types/dist/city-establishment/enum/establishment-offer.enum';
import { ESTABLISHMENT_OFFER_ORDER } from '@routellers/types/dist/city-establishment/enum/establishment-offer.enum';
import {
    ESTABLISHMENT_WEEKDAYS,
    type EstablishmentWeekday,
} from '@routellers/types/dist/city-establishment/establishment-hours';
import {
    INTAKE_AGES,
    INTAKE_BOOKINGS,
    INTAKE_CUISINES,
    INTAKE_DRINKS,
    INTAKE_PETS,
    INTAKE_ROOMS,
} from '@routellers/types/dist/city-establishment/establishment-intake';
import { specialtiesOf } from '@routellers/types/dist/city-establishment/establishment-specialties';
import { INTAKE_MARKETS } from '@routellers/types/dist/city-establishment/intake-market';
import type { CountriesEnum } from '@routellers/types/dist/shared/enum/countries.enum';
import { useTranslation } from 'react-i18next';
import { Section } from '../components/editor';
import type { HoursDayForm } from '../components/week-hours-fields';

export type IntakeShape = {
    title: string;
    offers: EstablishmentOfferEnum[];
    address: string;
    lat: string;
    lng: string;
    instagram: string;
    whatsapp: string;
    facebook: string;
    line: string;
    website: string;
    phone: string;
    operator: string;
    openedYear: string;
    priceLevel: string | null;
    closedNote: string;
    hours: Record<EstablishmentWeekday, HoursDayForm>;
    hoursNote: string;
    specialties: string[];
    cuisines: string[];
    drinks: string[];
    rooms: string[];
    pets: string;
    age: string;
    booking: string;
    cashOnly: string;
    photoCredit: string;
};

const pick = <T extends string>(values: string[], allowed: readonly T[]) =>
    values
        .filter((value): value is T => (allowed as readonly string[]).includes(value))
        .slice(0, 2);

const one = <T extends string>(value: string, allowed: readonly T[]): T | null =>
    (allowed as readonly string[]).includes(value) ? (value as T) : null;

export const intakeAnswers = (values: IntakeShape) => ({
    cuisines: pick(values.cuisines, INTAKE_CUISINES),
    drinks: pick(values.drinks, INTAKE_DRINKS),
    rooms: pick(values.rooms, INTAKE_ROOMS),
    pets: one(values.pets, INTAKE_PETS),
    age: one(values.age, INTAKE_AGES),
    booking: one(values.booking, INTAKE_BOOKINGS),
    cashOnly: values.cashOnly === 'yes' ? true : values.cashOnly === 'no' ? false : null,
    photoCredit: values.photoCredit.trim() || null,
    specialties: specialtiesOf(values.specialties),
});

const money = (value: number, language: string) =>
    new Intl.NumberFormat(language.startsWith('ru') ? 'ru-RU' : 'en', {
        maximumFractionDigits: 0,
    }).format(value);

export const EstablishmentIntakeTab = ({
    form,
    country,
    specialtyChoices,
    mapHref,
}: {
    form: UseFormReturnType<IntakeShape>;
    country: CountriesEnum | null;
    specialtyChoices: { value: string; label: string }[];
    mapHref: string | null;
}) => {
    const { t, i18n } = useTranslation();
    const market = country ? INTAKE_MARKETS[country] : undefined;
    const values = form.values;
    const amount = (value: number) => money(value, i18n.language);
    const priceOptions = market
        ? [
              {
                  value: '1',
                  label: t('pages.establishments.editor.spendUnder', {
                      amount: amount(market.bands[0]),
                      currency: market.currency,
                  }),
              },
              {
                  value: '2',
                  label: `${amount(market.bands[0])}–${amount(market.bands[1])}`,
              },
              {
                  value: '3',
                  label: `${amount(market.bands[1])}–${amount(market.bands[2])}`,
              },
              {
                  value: '4',
                  label: t('pages.establishments.editor.spendOver', {
                      amount: amount(market.bands[2]),
                      currency: market.currency,
                  }),
              },
          ]
        : [1, 2, 3, 4].map((level) => ({
              value: String(level),
              label: t(`enums.establishmentPrice.${level}`),
          }));

    const patchHour = (day: EstablishmentWeekday, patch: Partial<HoursDayForm>) => {
        const hours = {
            ...values.hours,
            [day]: { ...values.hours[day], ...patch },
        };

        form.setFieldValue('hours', hours);
    };

    const copyWeekdays = () => {
        const monday = { ...values.hours.mon };
        const hours = { ...values.hours };

        for (const day of ['tue', 'wed', 'thu', 'fri'] as const) hours[day] = { ...monday };
        form.setFieldValue('hours', hours);
    };

    const setLimited = (field: 'cuisines' | 'drinks' | 'rooms', next: string[]) => {
        if (next.length > 2) return;
        form.setFieldValue(field, next);
    };

    return (
        <Stack gap='md'>
            <Text size='sm' c='dimmed'>
                {t('pages.establishments.editor.intakeLead')}
            </Text>

            <Section title={t('pages.establishments.editor.door')} boxed>
                <TextInput
                    label={t('pages.establishments.editor.doorName')}
                    description={t('pages.establishments.editor.doorNameHint')}
                    required
                    {...form.getInputProps('title')}
                />
                <TextInput
                    label={t('pages.establishments.editor.address')}
                    {...form.getInputProps('address')}
                />
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing='sm'>
                    <TextInput
                        label={t('pages.establishments.editor.lat')}
                        {...form.getInputProps('lat')}
                    />
                    <TextInput
                        label={t('pages.establishments.editor.lng')}
                        {...form.getInputProps('lng')}
                    />
                </SimpleGrid>
                {mapHref ? (
                    <Anchor href={mapHref} target='_blank' size='xs'>
                        {t('pages.establishments.editor.openMap')}
                    </Anchor>
                ) : null}
            </Section>

            <Section
                title={t('pages.establishments.editor.reach')}
                description={t('pages.establishments.editor.contactsHint')}
                boxed
            >
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing='sm'>
                    <TextInput
                        label={t('pages.establishments.editor.whatsapp')}
                        {...form.getInputProps('whatsapp')}
                    />
                    <TextInput
                        label={t('pages.establishments.editor.facebook')}
                        {...form.getInputProps('facebook')}
                    />
                    <TextInput
                        label={t('pages.establishments.editor.instagram')}
                        {...form.getInputProps('instagram')}
                    />
                    <TextInput
                        label={t('pages.establishments.editor.line')}
                        {...form.getInputProps('line')}
                    />
                    <TextInput
                        label={t('pages.establishments.editor.phone')}
                        {...form.getInputProps('phone')}
                    />
                    <TextInput
                        label={t('pages.establishments.editor.website')}
                        {...form.getInputProps('website')}
                    />
                    <TextInput
                        label={t('pages.establishments.editor.operator')}
                        description={t('pages.establishments.editor.operatorHint')}
                        {...form.getInputProps('operator')}
                    />
                    <TextInput
                        label={t('pages.establishments.editor.openedYear')}
                        {...form.getInputProps('openedYear')}
                    />
                </SimpleGrid>
            </Section>

            <Section
                title={t('pages.establishments.editor.offers')}
                description={t('pages.establishments.editor.offersHint')}
                boxed
            >
                <Checkbox.Group
                    value={values.offers}
                    onChange={(next) => form.setFieldValue('offers', next as IntakeShape['offers'])}
                >
                    <Group gap='sm' mt={4}>
                        {ESTABLISHMENT_OFFER_ORDER.map((offer) => (
                            <Checkbox
                                key={offer}
                                value={offer}
                                label={t(`enums.establishmentOffer.${offer}`)}
                            />
                        ))}
                    </Group>
                </Checkbox.Group>
            </Section>

            <Section title={t('pages.establishments.editor.spend')} boxed>
                <Radio.Group
                    value={values.priceLevel ?? ''}
                    onChange={(value) => form.setFieldValue('priceLevel', value || null)}
                >
                    <Group gap='md' mt={4}>
                        {priceOptions.map((option) => (
                            <Radio key={option.value} value={option.value} label={option.label} />
                        ))}
                        <Radio value='' label={t('pages.establishments.editor.spendUnset')} />
                    </Group>
                </Radio.Group>
            </Section>

            <Section title={t('pages.establishments.editor.specialties')} boxed>
                <MultiSelect
                    description={t('pages.establishments.editor.specialtiesHint')}
                    data={specialtyChoices}
                    searchable
                    clearable
                    maxValues={2}
                    hidePickedOptions
                    {...form.getInputProps('specialties')}
                />
            </Section>

            <Section boxed>
                <LimitedChecks
                    label={t('pages.establishments.editor.cuisine')}
                    value={values.cuisines}
                    options={INTAKE_CUISINES.map((code) => ({
                        value: code,
                        label: t(`enums.intakeCuisine.${code}`),
                    }))}
                    onChange={(next) => setLimited('cuisines', next)}
                />
                <LimitedChecks
                    label={t('pages.establishments.editor.drink')}
                    value={values.drinks}
                    options={INTAKE_DRINKS.map((code) => ({
                        value: code,
                        label: t(`enums.intakeDrink.${code}`),
                    }))}
                    onChange={(next) => setLimited('drinks', next)}
                />
                <LimitedChecks
                    label={t('pages.establishments.editor.room')}
                    value={values.rooms}
                    options={INTAKE_ROOMS.map((code) => ({
                        value: code,
                        label: t(`enums.intakeRoom.${code}`),
                    }))}
                    onChange={(next) => setLimited('rooms', next)}
                />
            </Section>

            <Section title={t('pages.establishments.editor.rules')} boxed>
                <RadioRow
                    label={t('pages.establishments.editor.pets')}
                    value={values.pets}
                    onChange={(value) => form.setFieldValue('pets', value)}
                    options={[
                        { value: '', label: t('pages.establishments.editor.unset') },
                        ...INTAKE_PETS.map((code) => ({
                            value: code,
                            label: t(`enums.intakePets.${code}`),
                        })),
                    ]}
                />
                <RadioRow
                    label={t('pages.establishments.editor.age')}
                    hint={t('pages.establishments.editor.ageHint')}
                    value={values.age}
                    onChange={(value) => form.setFieldValue('age', value)}
                    options={[
                        { value: '', label: t('pages.establishments.editor.unset') },
                        { value: 'all', label: t('enums.intakeAge.all') },
                        {
                            value: 'restricted',
                            label: t('pages.establishments.editor.ageRestricted', {
                                age: market?.drinkingAge ?? 18,
                            }),
                        },
                    ]}
                />
                <RadioRow
                    label={t('pages.establishments.editor.booking')}
                    value={values.booking}
                    onChange={(value) => form.setFieldValue('booking', value)}
                    options={[
                        { value: '', label: t('pages.establishments.editor.unset') },
                        ...INTAKE_BOOKINGS.map((code) => ({
                            value: code,
                            label: t(`enums.intakeBooking.${code}`),
                        })),
                    ]}
                />
                <RadioRow
                    label={t('pages.establishments.editor.cash')}
                    value={values.cashOnly}
                    onChange={(value) => form.setFieldValue('cashOnly', value)}
                    options={[
                        { value: '', label: t('pages.establishments.editor.unset') },
                        ...(['no', 'yes'] as const).map((code) => ({
                            value: code,
                            label: t(`enums.intakeCash.${code}`),
                        })),
                    ]}
                />
            </Section>

            <Section
                title={t('pages.establishments.editor.hours')}
                description={t('pages.establishments.editor.hoursHint')}
                boxed
            >
                <Group justify='flex-end'>
                    <Button variant='subtle' size='compact-sm' onClick={copyWeekdays}>
                        {t('pages.establishments.editor.copyWeekdays')}
                    </Button>
                </Group>
                <HoursGrid hours={values.hours} onPatch={patchHour} />
                <Textarea
                    label={t('pages.establishments.editor.hoursNote')}
                    description={t('pages.establishments.editor.hoursNoteHint')}
                    autosize
                    minRows={2}
                    {...form.getInputProps('hoursNote')}
                />
                <Textarea
                    label={t('pages.establishments.editor.closedTemporary')}
                    autosize
                    minRows={2}
                    {...form.getInputProps('closedNote')}
                />
            </Section>

            <Section boxed>
                <TextInput
                    label={t('pages.establishments.editor.photoCredit')}
                    maxLength={120}
                    {...form.getInputProps('photoCredit')}
                />
            </Section>
        </Stack>
    );
};

function HoursGrid({
    hours,
    onPatch,
}: {
    hours: Record<EstablishmentWeekday, HoursDayForm>;
    onPatch: (day: EstablishmentWeekday, patch: Partial<HoursDayForm>) => void;
}) {
    const { t } = useTranslation();

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: '28px max-content minmax(0, 1fr) minmax(0, 1fr)',
                columnGap: 8,
                rowGap: 6,
                alignItems: 'center',
            }}
        >
            <span />
            <span />
            <Text size='xs' c='dimmed' ta='center'>
                {t('pages.establishments.editor.opens')}
            </Text>
            <Text size='xs' c='dimmed' ta='center'>
                {t('pages.establishments.editor.closes')}
            </Text>
            {ESTABLISHMENT_WEEKDAYS.flatMap((day) => {
                const row = hours[day];

                return [
                    <Text key={`${day}-d`} size='sm' fw={600}>
                        {t(`enums.weekday.${day}`)}
                    </Text>,
                    <Checkbox
                        key={`${day}-c`}
                        size='xs'
                        label={t('pages.establishments.editor.closedDay')}
                        checked={row.closed}
                        onChange={(event) => onPatch(day, { closed: event.currentTarget.checked })}
                    />,
                    <TextInput
                        key={`${day}-o`}
                        type='time'
                        size='xs'
                        styles={{ input: { minWidth: 0 } }}
                        value={row.open}
                        disabled={row.closed}
                        onChange={(event) => onPatch(day, { open: event.currentTarget.value })}
                    />,
                    <TextInput
                        key={`${day}-l`}
                        type='time'
                        size='xs'
                        styles={{ input: { minWidth: 0 } }}
                        value={row.close}
                        disabled={row.closed}
                        onChange={(event) => onPatch(day, { close: event.currentTarget.value })}
                    />,
                ];
            })}
        </div>
    );
}

function LimitedChecks({
    label,
    value,
    options,
    onChange,
}: {
    label: string;
    value: string[];
    options: { value: string; label: string }[];
    onChange: (next: string[]) => void;
}) {
    return (
        <Checkbox.Group label={label} value={value} onChange={onChange}>
            <Group gap='sm' mt={4}>
                {options.map((option) => (
                    <Checkbox key={option.value} value={option.value} label={option.label} />
                ))}
            </Group>
        </Checkbox.Group>
    );
}

function RadioRow({
    label,
    hint,
    value,
    onChange,
    options,
}: {
    label: string;
    hint?: string;
    value: string;
    onChange: (value: string) => void;
    options: { value: string; label: string }[];
}) {
    return (
        <Radio.Group label={label} description={hint} value={value} onChange={onChange}>
            <Group gap='md' mt={4}>
                {options.map((option) => (
                    <Radio
                        key={option.value || 'unset'}
                        value={option.value}
                        label={option.label}
                    />
                ))}
            </Group>
        </Radio.Group>
    );
}
